import { isProblemError } from '../../api/client';

/**
 * O que uma lista da tela de presenca pode estar dizendo — e nada alem disso.
 *
 * O tipo existe para tornar o defeito anterior inexprimivel. `query.data ?? []`
 * fundia quatro situacoes num unico `[]`: ainda carregando, o servidor recusou,
 * a chamada falhou, e ninguem compareceu. A tela renderizava as quatro como
 * lista vazia, que a pessoa le como "ninguem compareceu" — a unica das quatro
 * que era afirmacao sobre a aula, e nao sobre a requisicao.
 *
 * Aqui `items` so existe em `loaded`, e `loaded` so nasce com pelo menos um
 * elemento: nao ha como escrever a lista vazia que mente.
 */
export type ListState<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'refused' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'empty' }
  | { readonly kind: 'loaded'; readonly items: readonly [T, ...T[]] };

/** O recorte de `UseQueryResult` que decide o estado. Qualquer resultado serve. */
export interface QuerySnapshot<T> {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: readonly T[] | undefined;
}

export function listState<T>(query: QuerySnapshot<T>): ListState<T> {
  // Erro antes de pendente: com `retry: false` o resultado com erro nao esta
  // mais carregando, e mostrar o giro sobre uma falha e a mesma mentira de
  // antes, so que mais lenta.
  if (query.isError) return { kind: isRefusal(query.error) ? 'refused' : 'failed' };
  if (query.isPending || query.data === undefined) return { kind: 'loading' };

  const [first, ...rest] = query.data;
  return first === undefined ? { kind: 'empty' } : { kind: 'loaded', items: [first, ...rest] };
}

/**
 * 403 e 404 sao decisao do servidor sobre **quem pergunta**, e nao acidente de
 * rede: perguntar de novo devolve a mesma recusa. A distincao existe para a
 * tela nao oferecer "tentar de novo" a quem nao vai passar — e e ela que faz a
 * aluna recusada nesta rota ver uma recusa em vez de uma lista vazia.
 */
function isRefusal(error: unknown): boolean {
  return isProblemError(error) && (error.status === 403 || error.status === 404);
}
